import React, { useState } from "react";
import { Carousel, Col, Container, Row } from "react-bootstrap";
import MultiCarousel, { ResponsiveType } from "react-multi-carousel";
import "react-multi-carousel/lib/styles.css";
import { Link } from "react-router-dom";

import DOMPurify from "dompurify";

import TournamentOfferings from "../Tournaments/TournamentOfferings";

import { store } from "../..";
import "../../assets/css/wibb/pages/home.scss";
import ImageModal from "../../components/ImageModal";
import {
    globalActions,
    selectSetting,
} from "../../features/global/globalSlice";
import { tournamentManagementActions } from "../../features/tourneymgmt/tournamentManagementSlice";
import { useAppSelector } from "../../hooks/reduxHooks";
import { UPLOADED_IMAGE_URL } from "../../lib/globals";
import { MagnifyingGlassIcon } from "../../lib/icons";
import { IImage } from "../../types/shared";

export const loader = async () => {
    const settings = store.getState().global.settings;
    const photos = store.getState().global.photos;
    const masterTournaments =
        store.getState().tournamentmanagement.masterTournaments;
    const tournaments = store.getState().tournamentmanagement.tournaments;

    const settingsPromise = settings.length
        ? Promise.resolve()
        : store.dispatch(globalActions.getSettings());

    const masterTournamentsPromise = masterTournaments.length
        ? Promise.resolve()
        : store.dispatch(tournamentManagementActions.getMasterTournaments());

    const tournamentsPromise = tournaments.length
        ? Promise.resolve()
        : store.dispatch(tournamentManagementActions.getTournaments());

    const photosPromise = photos.length
        ? Promise.resolve()
        : store.dispatch(globalActions.getPhotos());

    await Promise.all([
        photosPromise,
        settingsPromise,
        masterTournamentsPromise,
        tournamentsPromise,
    ]);

    return null;
};

const HomePage = (): React.JSX.Element => {
    const [modalShow, setModalShow] = useState<boolean>(false);
    const [modalContent, setModalContent] = useState<IImage>({
        image: "",
        alt: "",
    });

    const photos = useAppSelector(state => state.global.photos);

    const [
        homePageRedBoxContent,
        homePageMainContentBlock,
        homePageCompetitionLevels,
        homePageTestimonials,
        masterTournaments,
        tournaments,
    ] = [
        useAppSelector(state => selectSetting(state, "wibb_home_page_red_box")),
        useAppSelector(state => selectSetting(state, "wibb_home_content_1")),
        useAppSelector(state =>
            selectSetting(state, "wibb_home_page_competition_levels"),
        ),
        useAppSelector(state =>
            selectSetting(state, "wibb_home_page_testimonials"),
        ),
        useAppSelector(state => state.tournamentmanagement.masterTournaments),
        useAppSelector(state => state.tournamentmanagement.tournaments),
    ];

    const carousel1Images: IImage[] = photos
        .filter(o => o.meta.location?.id === 4 && o.approvalDate !== null)
        .map(p => {
            return { image: p.photo, alt: p.meta.caption };
        });

    const carousel2Images: IImage[] = photos
        .filter(o => o.meta.location?.id === 5 && o.approvalDate !== null)
        .map(p => {
            return { image: p.photo, alt: p.meta.caption };
        });

    const responsive: ResponsiveType = {
        desktop: {
            breakpoint: { max: 3000, min: 1024 },
            items: 4,
        },
        tablet: {
            breakpoint: { max: 1024, min: 464 },
            items: 2,
        },
        mobile: {
            breakpoint: { max: 464, min: 0 },
            items: 1,
        },
    };

    const tournamentOfferings = masterTournaments.filter(t => {
        return new Date(t.startDate) > new Date() && t.active;
    });

    return (
        <Container data-testid="homePageContent" className="homePage">
            <Row className="pt-2 pt-lg-5">
                <Col xs={12} lg={8}>
                    <Carousel
                        fade
                        controls={false}
                        indicators={false}
                        interval={10000}
                    >
                        {carousel1Images.map((i: IImage, idx: number) => (
                            <Carousel.Item key={idx}>
                                <img
                                    src={`${UPLOADED_IMAGE_URL}${i.image}`}
                                    alt={i.alt}
                                />
                            </Carousel.Item>
                        ))}
                    </Carousel>
                </Col>
                <Col xs={12} lg={4} className="pt-2 pt-lg-0">
                    <div
                        className="red-highlight py-4 px-2"
                        dangerouslySetInnerHTML={{
                            __html: DOMPurify.sanitize(
                                homePageRedBoxContent?.setting,
                            ),
                        }}
                    />
                </Col>
            </Row>

            <Row>
                <Col>
                    <div
                        className="p-4 mainInfo"
                        dangerouslySetInnerHTML={{
                            __html: DOMPurify.sanitize(
                                homePageMainContentBlock?.setting,
                            ),
                        }}
                    />
                </Col>
            </Row>

            <Row>
                <Col>
                    <section
                        className="mainInfo redBg"
                        dangerouslySetInnerHTML={{
                            __html: DOMPurify.sanitize(
                                homePageCompetitionLevels?.setting,
                            ),
                        }}
                    />
                </Col>
            </Row>

            <TournamentOfferings
                tournamentOfferings={tournamentOfferings}
                tournaments={tournaments}
            />

            <Row className="redBg">
                <Col>
                    <div
                        className="p-4 mainInfo"
                        dangerouslySetInnerHTML={{
                            __html: DOMPurify.sanitize(
                                homePageTestimonials?.setting,
                            ),
                        }}
                    />
                </Col>
            </Row>

            <Row>
                <Col>
                    <MultiCarousel
                        responsive={responsive}
                        swipeable={false}
                        draggable={false}
                        infinite={true}
                        containerClass="multiCarousel"
                    >
                        {carousel2Images.map((i: IImage, idx: number) => (
                            <div
                                className="carouselImgContainer"
                                key={`carousel-image-${idx}`}
                                onClick={() => {
                                    setModalContent(i);
                                    setModalShow(true);
                                }}
                            >
                                <img
                                    src={`${UPLOADED_IMAGE_URL}${i.image}`}
                                    alt={i.alt}
                                />
                                <span className="rollover">
                                    <MagnifyingGlassIcon />
                                </span>
                            </div>
                        ))}
                    </MultiCarousel>

                    <ImageModal
                        show={modalShow}
                        image={modalContent.image}
                        alt={modalContent.alt}
                        onHide={() => setModalShow(false)}
                    />
                    <h3>Submit a Wisconsin Baseball Tournament photo!</h3>
                    <p>
                        Do you have a photo that you would like to display on
                        the Wisconsin Baseball Tournament website? Upload your
                        photos
                        <Link to="/photo-upload">here!</Link>
                    </p>
                </Col>
            </Row>
        </Container>
    );
};

export default HomePage;
